import CreateAxiosInstance from './axiosConfig';

// useAuth: false. /geo/me is a no_auth route, and the auth path in the request
// interceptor does `await clerk.load()` — which would put this request BEHIND Clerk
// (measured: clerk +1196ms, request +1473ms) on the critical path for LaunchDarkly
// identify, which awaits this before building its context.
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL, false);

// The only bound that actually holds. axios's own `timeout` starts after the request
// interceptor resolves, and that interceptor awaits getAPIBaseURL(), which on preview
// hosts makes an unbounded /health fetch — so on a blackholed PR backend the axios
// timeout never arms and LD identify waits forever.
const TIMEOUT_MS = 3000;

// A resolved miss ('UNKNOWN') is worth re-asking on a later visit, but not on every
// page load: an IP ipinfo cannot place to a state would otherwise spend a lookup
// per navigation forever.
const RETRY_AFTER_MS = 24 * 60 * 60 * 1000;

// 'UNKNOWN' means we asked and got nothing back. It is a cached miss, not a value.
const isGood = (value) => !!value && value !== 'UNKNOWN';

const read = () => ({
  country_code: localStorage.getItem('country_code'),
  country_name: localStorage.getItem('country_name'),
  region: localStorage.getItem('region'),
  region_code: localStorage.getItem('region_code'),
});

// Region is only resolved for the US — the backend spends its paid lookup there and
// nowhere else — so a good non-US country is a complete answer. Requiring a region of
// everyone would make every non-US visitor re-fetch on every load, forever.
const isComplete = (cached) => {
  if (!isGood(cached.country_code) || !isGood(cached.country_name)) return false;
  return cached.country_code !== 'US' || (isGood(cached.region) && isGood(cached.region_code));
};

const seedMissing = (cached) => {
  Object.entries(cached).forEach(([key, value]) => {
    if (!value) localStorage.setItem(key, 'UNKNOWN');
  });
};

// The geo attributes for a LaunchDarkly context or a Mixpanel event. Unresolved keys
// are ABSENT, not 'UNKNOWN' and not null.
//
// Both distinctions matter. In LD, a literal 'UNKNOWN' makes `region exists` true for
// every visitor we couldn't place, so a negative clause ("region is not Virginia")
// silently sweeps them all in; an absent attribute matches no region rule at all,
// which is what a flag author means by "target Virginia". In Mixpanel, 'UNKNOWN' and
// null are two buckets for the same fact — nobody resolved a region — so slicing an
// impression by region splits the unresolved population in half.
//
// Deliberately country_code + region only: this is the shape base.jsx already sends,
// and widening the LD context is a targeting change, not a cleanup.
export function getResolvedGeo() {
  const cached = read();
  return {
    ...(isGood(cached.country_code) && { country_code: cached.country_code }),
    ...(isGood(cached.region) && { region: cached.region }),
  };
}

// What callers send to endpoints that would otherwise resolve geo from the IP
// themselves. Undefined rather than null for the misses — axios drops undefined
// params, and a literal 'UNKNOWN' would read as a real country on the server.
export function getCachedGeo() {
  const cached = read();
  return {
    country: isGood(cached.country_name) ? cached.country_name : undefined,
    state: isGood(cached.region) ? cached.region : undefined,
    stateCode: isGood(cached.region_code) ? cached.region_code : undefined,
  };
}

export async function resolveGeo() {
  const cached = read();
  if (isComplete(cached)) return;

  const checkedAt = Number(localStorage.getItem('geo_checked_at'));
  if (checkedAt && Date.now() - checkedAt < RETRY_AFTER_MS) return;

  try {
    // customErrorHandler suppresses the global error modal: the response interceptor
    // routes every unhandled rejection to it, and a slow failure here would land after
    // ErrorProvider mounts and throw a "Something went wrong" dialog over first paint.
    const { data } = await APIClient.get('/geo/me', {
      timeout: TIMEOUT_MS,
      customErrorHandler: () => {},
    });

    if (!data?.country_code) {
      // The backend resolved nothing — a private/bogon client IP, or ipinfo was down.
      // Never downgrade a country resolved on an earlier visit: it feeds the
      // X-Country-Code header and country-targeted LD rules.
      seedMissing(cached);
    } else {
      // A country came back, so this answer is authoritative — including its silences.
      // Writing 'UNKNOWN' over a stale region matters for anyone who had one cached
      // from the old browser-side lookup but is outside the US, where we no longer
      // resolve region at all.
      localStorage.setItem('country_code', data.country_code);
      localStorage.setItem('country_name', data.country || 'UNKNOWN');
      localStorage.setItem('region', data.region || 'UNKNOWN');
      localStorage.setItem('region_code', data.region_code || 'UNKNOWN');
    }
    // Only a real response starts the backoff clock. A network failure retries on the
    // next load, as it did when this lived in base.jsx.
    localStorage.setItem('geo_checked_at', String(Date.now()));
  } catch (error) {
    // First-time visitors still end up with a defined X-Country-Code, exactly as before.
    seedMissing(cached);
    console.error('Error setting geo:', error);
  }
}

// Started at bootstrap and awaited by the LaunchDarkly identify effect, which has no
// try/catch of its own: a rejection here would strand isLDidentified and leave every
// route gated on it blank for the life of the tab. resolveGeo swallows everything, and
// the race gives back a promise that always settles.
let inFlight = null;

export function primeGeo() {
  if (!inFlight) {
    inFlight = Promise.race([
      resolveGeo(),
      new Promise((resolve) => setTimeout(resolve, TIMEOUT_MS)),
    ]).catch(() => {});
  }
  return inFlight;
}
